<script>
import { mapGetters } from 'vuex';
import ContactForm from './ContactForm.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ContactDeleteModal from 'dashboard/modules/contact/ContactDeleteModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';

export default {
  components: {
    NextButton,
    ContactForm,
    ComposeConversation,
    ContactMergeModal,
    ContactDeleteModal,
    VoiceCallButton,
    ContactLabels,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    // 保留声明,避免父组件传入时 fallthrough 到根元素
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  data() {
    return {
      cardStatusOptions: ['', '未识别已同步', '已查看无留联'],
      selectedCardStatus: '',
      showCardStatusDropdown: false,
    };
  },
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    contactCustomAttributes() {
      return this.contact.custom_attributes || {};
    },
    cardStatusColorClass() {
      const status = this.selectedCardStatus;
      if (status === '未识别已同步') return 'bg-green-500';
      if (status === '已查看无留联') return 'bg-slate-400';
      return 'bg-transparent border border-solid border-n-slate-4';
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
    'contactCustomAttributes.card_status': {
      handler(val) {
        this.selectedCardStatus = val || '';
      },
      immediate: true,
    },
  },
  methods: {
    // 标准编辑表单(ContactForm)提交
    async onSubmitContact(contactItem) {
      await this.$store.dispatch('contacts/update', contactItem);
      await this.$store.dispatch('contacts/fetchContactableInbox', this.contact.id);
    },
    async selectCardStatus(opt) {
      this.selectedCardStatus = opt;
      this.showCardStatusDropdown = false;
      if (opt === '') return;
      await this.$store.dispatch('contacts/update', {
        id: this.contact.id,
        custom_attributes: {
          ...this.contactCustomAttributes,
          card_status: opt,
        },
      });
    },
  },
};
</script>

<template>
  <div class="relative w-full h-full overflow-auto">
    <div class="flex flex-col w-full gap-3 p-3 text-left rtl:text-right">
      <!-- 联系人卡片: 浅底+边框, 与下方分区视觉区分 -->
      <div class="flex flex-col rounded-xl bg-n-surface-1 border border-n-weak">
        <!-- 内联编辑联系人表单(替代只读联系人信息 + 浮层抽屉弹窗) -->
        <ContactForm
          :contact="contact"
          :in-progress="uiFlags.isUpdating"
          :on-submit="onSubmitContact"
        />

      <!-- 操作按钮 -->
      <div class="flex items-center w-full gap-2 px-8 pb-8">
        <ComposeConversation :contact-id="String(contact.id)">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
            />
          </template>
        </ComposeConversation>
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
        />
        <ContactMergeModal :primary-contact="contact">
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              sm
              :disabled="uiFlags.isMerging"
            />
          </template>
        </ContactMergeModal>
        <ContactDeleteModal
          v-if="isAdmin"
          :contact="contact"
          @deleted="$emit('panelClose')"
        >
          <template #trigger>
            <NextButton
              v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
              icon="i-ph-trash"
              slate
              faded
              sm
              ruby
              :disabled="uiFlags.isDeleting"
            />
          </template>
        </ContactDeleteModal>
      </div>
      </div>

      <!-- 标签区: 醒目标题, 会话右侧也能直接给联系人打标签 -->
      <div class="flex flex-col gap-2 rounded-xl bg-n-surface-1 border border-n-weak p-4">
        <div class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-n-slate-10">
          <span class="i-lucide-tag text-n-brand"></span> 标签
        </div>
        <ContactLabels v-if="contact.id" :contact-id="contact.id" />
      </div>
    </div>
  </div>
</template>
